import type {
  AthenaTransactionRedemptionView,
  Dispense,
  DispenseBill,
  LegacyRedemption,
  RedemptionMethodName,
  RedemptionObligationStatus,
  RedemptionOrigin,
  RedemptionOutcomeClass,
  RedemptionPaymentMethod,
  RedemptionSettlement,
  RedemptionSettlementStatus,
  TransactionRedemption,
} from '@/types';

/** `value` when it's one of `allowed`, else `fallback` -- keeps unknown strings out of contract enums. */
const oneOf = <T extends string | null>(
  value: string | null | undefined,
  allowed: readonly T[],
  fallback: T,
): T => (allowed.includes(value as T) ? (value as T) : fallback);

const OBLIGATION_STATUSES: readonly RedemptionObligationStatus[] = [
  'pending',
  'completed',
  'cancelled',
];
const SETTLEMENT_STATUSES: readonly RedemptionSettlementStatus[] = [
  'issued',
  'claimed',
  'fulfilled',
  'short',
  'cancelled',
  'unknown',
];
const ORIGINS: readonly RedemptionOrigin[] = ['dispense_failure', 'unreversed_debit'];
const PAYMENT_METHODS: readonly RedemptionPaymentMethod[] = ['cash', 'debit'];
const OUTCOME_CLASSES: readonly RedemptionOutcomeClass[] = [
  'refused',
  'withdrawn',
  'transient',
  'unknown',
];

// Unity renamed `superseded` to `short` (dev-2443); older rows may still carry the old name.
const toSettlementStatus = (status: string | null | undefined) =>
  oneOf(status === 'superseded' ? 'short' : status, SETTLEMENT_STATUSES, 'issued');

const toAmountString = (value: number | string | null | undefined, fallback: string) =>
  value != null ? String(value) : fallback;

export type RedemptionTabState =
  | 'noFailure'
  | 'noObligation'
  | 'pending'
  | 'issued'
  | 'unknownLock'
  | 'short'
  | 'fulfilled';

export const sumAssetUnits = (bills: DispenseBill[]): number =>
  bills.reduce((sum, bill) => sum + bill.quantity * bill.unit, 0);

// The backend sends zero-quantity bill rows (seen on successful_bills), so those are dropped
// here -- otherwise a clean dispense with a `quantity: 0` rejected row reads as a shortfall.
export const getRejectedBills = (dispenses: Dispense[]): DispenseBill[] =>
  dispenses.flatMap((d) => d.rejected_bills).filter((bill) => bill.quantity > 0);

export const deriveRedemptionState = (
  dispenses: Dispense[],
  redemption: TransactionRedemption | undefined | null,
): RedemptionTabState => {
  const shortfall = getRejectedBills(dispenses);
  const obligation = redemption?.obligation ?? null;
  const settlements = redemption?.settlements ?? [];

  if (shortfall.length === 0) return 'noFailure';
  if (obligation === null) return 'noObligation';
  if (obligation.status === 'completed') return 'fulfilled';
  if (settlements.some((s) => s.status === 'unknown')) return 'unknownLock';
  if (settlements.some((s) => s.status === 'issued' || s.status === 'claimed')) return 'issued';

  const newest = sortSettlementsNewestFirst(redemption)[0];
  if (newest?.status === 'short') return 'short';

  return 'pending';
};

export type RedemptionBadgeColor = 'red' | 'amber' | 'green' | null;

export const getRedemptionBadgeColor = (state: RedemptionTabState): RedemptionBadgeColor => {
  switch (state) {
    case 'pending':
    case 'short':
      return 'red';
    case 'issued':
    case 'unknownLock':
      return 'amber';
    case 'fulfilled':
      return 'green';
    default:
      return null;
  }
};

export const isRedemptionLocked = (state: RedemptionTabState): boolean =>
  state === 'issued' || state === 'unknownLock';

export const sortSettlementsNewestFirst = (redemption: TransactionRedemption | undefined | null) =>
  [...(redemption?.settlements ?? [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

export const getActiveSettlement = (
  redemption: TransactionRedemption | undefined | null,
): RedemptionSettlement | null =>
  sortSettlementsNewestFirst(redemption).find((s) =>
    ['issued', 'claimed', 'unknown'].includes(s.status),
  ) ?? null;

export const getHistorySettlements = (
  redemption: TransactionRedemption | undefined | null,
): RedemptionSettlement[] => {
  const active = getActiveSettlement(redemption);
  return sortSettlementsNewestFirst(redemption).filter((s) => s.id !== active?.id);
};

export const getLatestCancelledSettlement = (
  redemption: TransactionRedemption | undefined | null,
) => {
  const [latest] = sortSettlementsNewestFirst(redemption);
  return latest?.status === 'cancelled' ? latest : null;
};

export const formatRedemptionAmount = (
  method: string,
  assetAmount: number,
  fiatAmount?: string | number | null,
): string => {
  const gb = `${assetAmount} Goldback${assetAmount === 1 ? '' : 's'}`;
  const fiat = fiatAmount != null && fiatAmount !== '' ? `$${Number(fiatAmount).toFixed(2)}` : null;

  return method === 'debit_reversal' && fiat ? `${fiat} (${gb})` : gb;
};

// `saleRate` is the sale's own fiat-per-unit rate (TransactionDetail.rate) -- athena's embedded
// view carries no asset_rate/fiat_amount yet, and the obligation is priced at the sale's rate.
export const adaptLegacyRedemption = (
  legacy: LegacyRedemption | null | undefined,
  saleRate?: number | null,
): TransactionRedemption => {
  if (!legacy) return { obligation: null, settlements: [] };

  const assetRate = legacy.asset_rate ?? saleRate;
  const fiatAmount =
    legacy.fiat_amount ??
    legacy.usd_amount ??
    (assetRate != null ? legacy.asset_amount * Number(assetRate) : null);

  return {
    obligation: {
      id: legacy.id,
      // Unity's new-model fields pass through once athena's embedded view carries them; until
      // then (or for a value outside the contract enums) the pre-new-model defaults apply.
      status: oneOf(legacy.status, OBLIGATION_STATUSES, 'pending'),
      origin: oneOf(legacy.origin, ORIGINS, 'dispense_failure'),
      payment_method: oneOf(legacy.payment_method, PAYMENT_METHODS, 'cash'),
      asset_id: legacy.asset_id,
      asset_type: legacy.asset_type,
      asset_amount: legacy.asset_amount,
      outstanding_asset_amount: legacy.outstanding_asset_amount ?? legacy.asset_amount,
      asset_rate: toAmountString(assetRate, '0'),
      fiat_amount: toAmountString(fiatAmount, ''),
      fiat_type: legacy.fiat_type ?? 'USD',
      closed_at: legacy.closed_at ?? null,
      closed_by: legacy.closed_by ?? null,
      created_at: legacy.created_at,
      updated_at: legacy.updated_at ?? legacy.created_at,
    },
    settlements: legacy.settlements.map((s, index) => ({
      id: s.id,
      redemption_obligation_id: legacy.id,
      method: (s.method ?? '') as RedemptionMethodName,
      status: toSettlementStatus(s.status),
      attempt_no: s.attempt_number ?? index + 1,
      asset_id: s.asset_id ?? null,
      asset_type: s.asset_type ?? null,
      asset_amount: s.asset_amount ?? legacy.asset_amount,
      // Assumes Unity's settled_asset_amount is what the contract calls delivered_asset_amount.
      delivered_asset_amount: s.settled_asset_amount ?? null,
      fiat_amount: toAmountString(s.fiat_amount, ''),
      fiat_type: s.fiat_type ?? 'USD',
      reference: s.reference ?? null,
      dispense_id: s.dispense_id ?? null,
      // Unity's method_data shape isn't confirmed against the contract's per-method types yet.
      method_data: s.method_data ?? null,
      outcome_class: oneOf(s.outcome_class, OUTCOME_CLASSES, null),
      outcome_code: s.outcome_code ?? null,
      resolved_at: s.resolved_at ?? null,
      resolved_by: s.resolved_by ?? null,
      resolved_reason: s.resolved_reason ?? null,
      notes: s.notes ?? null,
      created_by: s.created_by ?? null,
      created_at: s.created_at,
      updated_at: s.updated_at ?? s.created_at,
    })),
  };
};

/**
 * Normalizes athena's dedicated redemption endpoint into the contract shape. Its obligation and
 * settlements use the same Unity names the embedded view did, so they go through the same
 * adapter -- which also backfills the fields athena omits when null (outstanding_asset_amount
 * defaults to asset_amount, etc.). Contract-named settlement fields win when present.
 */
export const normalizeTransactionRedemption = (
  view: AthenaTransactionRedemptionView | null | undefined,
  saleRate?: number | null,
): TransactionRedemption => {
  if (!view?.obligation) return { obligation: null, settlements: [] };

  const settlements = view.settlements ?? [];
  const adapted = adaptLegacyRedemption(
    {
      ...view.obligation,
      settlements: settlements.map((s) => ({
        ...s,
        attempt_number: s.attempt_number ?? s.attempt_no,
        settled_asset_amount: s.settled_asset_amount ?? s.delivered_asset_amount,
      })),
    },
    saleRate,
  );

  return {
    ...adapted,
    ...(view.origin_sale_id != null && { origin_sale_id: view.origin_sale_id }),
    ...(view.origin_sale_uuid && { origin_sale_uuid: view.origin_sale_uuid }),
  };
};
